import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from 'rxjs';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { TranslatePipe } from "@ngx-translate/core";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { ITaxSettingsFormGroup } from "../../../../../models/properties/ITaxSettingsFormGroup";
import { CommonDataService } from "../../../../../services/common-data.service";
import { CONSTANTS } from "../../../../../Constants/Constants";
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';

@Component({
  selector: 'app-sol-properties-tax-settings',
  templateUrl: './sol-properties-tax-settings.component.html',
  styleUrls: ['./sol-properties-tax-settings.component.scss'],
  imports: [
    MatCheckboxModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatIconModule,
    MatTooltipModule
  ]
})
export class SolpropertiesTaxSettingsComponent implements OnInit {
  taxSettingsForm!: FormGroup<ITaxSettingsFormGroup>
  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly solPropertiesService: SolPropertiesService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly commonDataService: CommonDataService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeFormValueChanges();
    this.setupReadOnlyMode();
  }

  private subscribeFormValueChanges(): void {
    this.taxSettingsForm.valueChanges
    .pipe(take(CONSTANTS.ONE))
    .subscribe(
      () => this.solPropertiesService.markPropertiesChanged()
    );
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isReadOnly => {
        if (isReadOnly) {
          this.taxSettingsForm.disable({ emitEvent: false });
        } else {
          this.taxSettingsForm.enable({ emitEvent: false });
          this.reapplyAlwaysDisabledControls();
        }
      });
  }

  private reapplyAlwaysDisabledControls(): void {
    this.taxSettingsForm.controls.useTaxLabor.disable({ emitEvent: false });
    this.taxSettingsForm.controls.useTaxSubcontractors.disable({ emitEvent: false });
    this.taxSettingsForm.controls.useTaxMiscTravel.disable({ emitEvent: false });
  }

  initializeForm(): void {
    const otherMetaData = this.solPropertiesService.getOtherMetaDataModel();
    this.taxSettingsForm = this.formBuilder.group<ITaxSettingsFormGroup>({
      useTaxLabor: this.formBuilder.control<boolean>({ value: otherMetaData.taxSettings.useTaxLabor, disabled: true }),
      salesTaxLabor: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxLabor),
      useTaxMaterial: this.formBuilder.control<boolean>(otherMetaData.taxSettings.useTaxMaterial),
      salesTaxMaterial: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxMaterial),
      useTaxShipping: this.formBuilder.control<boolean>(otherMetaData.taxSettings.useTaxShippingAndHandling),
      salesTaxShipping: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxShippingAndHandling),
      useTaxWarranty: this.formBuilder.control<boolean>(otherMetaData.taxSettings.useTaxWarranty),
      salesTaxWarranty: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxWarranty),
      useTaxSubcontractors: this.formBuilder.control<boolean>({ value: otherMetaData.taxSettings.useTaxSubcontractors, disabled: true }),
      salesTaxSubcontractors: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxSubcontractors),
      useTaxMiscTravel: this.formBuilder.control<boolean>({ value: otherMetaData.taxSettings.useTaxMiscAndTravel, disabled: true }),
      salesTaxMiscTravel: this.formBuilder.control<boolean>(otherMetaData.taxSettings.salesTaxMiscAndTravel)
    });
    this.registerFormChangeHandlers();
  }

  private registerFormChangeHandlers(): void {
    const taxSettings = this.solPropertiesService.getOtherMetaDataModel().taxSettings;

    this.taxSettingsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(values => {
        taxSettings.useTaxMaterial = values.useTaxMaterial;
        taxSettings.salesTaxLabor = values.salesTaxLabor;
        taxSettings.salesTaxMaterial = values.salesTaxMaterial;
        taxSettings.useTaxShippingAndHandling = values.useTaxShipping;
        taxSettings.salesTaxShippingAndHandling = values.salesTaxShipping;
        taxSettings.useTaxWarranty = values.useTaxWarranty;
        taxSettings.salesTaxWarranty = values.salesTaxWarranty;
        taxSettings.salesTaxSubcontractors = values.salesTaxSubcontractors;
        taxSettings.salesTaxMiscAndTravel = values.salesTaxMiscTravel;
      });
  }
}
